import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { AlertTriangle, XCircle, Lock } from "lucide-react";
import { cn } from "@/lib/utils";
import type { SubscriptionTier } from "@/types/billing";
import { TIER_DISPLAY } from "@/types/billing";

/**
 * UpgradePrompt
 * Context-aware upgrade prompt component that shows different messages
 * based on the type of prompt needed:
 * - budget-warning: 80% usage warning (toast-style)
 * - budget-exhausted: 100% usage, action required
 * - feature-locked: feature requires a higher tier
 */

interface UpgradePromptProps {
  type: "budget-warning" | "budget-exhausted" | "feature-locked";
  currentTier: SubscriptionTier;
  feature?: string;
  onAddCredits?: () => void;
  className?: string;
}

export const UpgradePrompt: React.FC<UpgradePromptProps> = ({
  type,
  currentTier,
  feature,
  onAddCredits,
  className,
}) => {
  const navigate = useNavigate();

  // Determine the next tier for upgrade suggestions
  const tierOrder: SubscriptionTier[] = ["free", "basic", "standard", "pro", "enterprise"];
  const currentIndex = tierOrder.indexOf(currentTier);
  const nextTier = currentIndex < tierOrder.length - 1 ? tierOrder[currentIndex + 1] : null;
  const nextTierDisplay = nextTier ? TIER_DISPLAY[nextTier] : null;

  if (type === "budget-warning") {
    return (
      <div
        className={cn(
          "flex items-center gap-3 rounded-lg border border-yellow-300 bg-yellow-50 px-4 py-3 dark:border-yellow-700 dark:bg-yellow-950/30",
          className,
        )}
        role="alert"
      >
        <AlertTriangle className="h-5 w-5 flex-shrink-0 text-yellow-600 dark:text-yellow-400" />
        <div className="flex-1">
          <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
            You've used 80% of your AI budget
          </p>
          <p className="text-xs text-yellow-700 dark:text-yellow-300">
            Consider upgrading or purchasing extra credits to avoid interruptions.
          </p>
        </div>
        <div className="flex gap-2">
          {onAddCredits && (
            <Button
              variant="outline"
              size="sm"
              onClick={onAddCredits}
              className="border-yellow-400 text-yellow-700 hover:bg-yellow-100 dark:border-yellow-600 dark:text-yellow-300 dark:hover:bg-yellow-900/40"
            >
              Add Credits
            </Button>
          )}
          {nextTier && (
            <Button
              size="sm"
              onClick={() => navigate("/pricing")}
              className="bg-yellow-600 text-white hover:bg-yellow-700"
            >
              Upgrade
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (type === "budget-exhausted") {
    return (
      <div
        className={cn(
          "flex items-center gap-3 rounded-lg border border-red-300 bg-red-50 px-4 py-3 dark:border-red-700 dark:bg-red-950/30",
          className,
        )}
        role="alert"
      >
        <XCircle className="h-5 w-5 flex-shrink-0 text-red-600 dark:text-red-400" />
        <div className="flex-1">
          <p className="text-sm font-medium text-red-800 dark:text-red-200">
            AI budget exhausted
          </p>
          <p className="text-xs text-red-700 dark:text-red-300">
            Your AI budget has been fully used. Add credits or upgrade your plan to continue.
          </p>
        </div>
        <div className="flex gap-2">
          {onAddCredits && (
            <Button
              variant="outline"
              size="sm"
              onClick={onAddCredits}
              className="border-red-400 text-red-700 hover:bg-red-100 dark:border-red-600 dark:text-red-300 dark:hover:bg-red-900/40"
            >
              Add Credits
            </Button>
          )}
          {nextTier && (
            <Button
              size="sm"
              onClick={() => navigate("/pricing")}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Upgrade
            </Button>
          )}
        </div>
      </div>
    );
  }

  // feature-locked
  return (
    <div
      className={cn(
        "flex items-center gap-3 rounded-lg border border-blue-300 bg-blue-50 px-4 py-3 dark:border-blue-700 dark:bg-blue-950/30",
        className,
      )}
      role="alert"
    >
      <Lock className="h-5 w-5 flex-shrink-0 text-blue-600 dark:text-blue-400" />
      <div className="flex-1">
        <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
          {feature
            ? `Upgrade to ${nextTierDisplay?.name || "a higher plan"} to unlock ${feature}`
            : `This feature requires a ${nextTierDisplay?.name || "higher"} plan`}
        </p>
        {nextTierDisplay && (
          <p className="text-xs text-blue-700 dark:text-blue-300">
            {nextTierDisplay.description} - starting at ${nextTierDisplay.monthlyPrice}/mo
          </p>
        )}
      </div>
      <Button
        size="sm"
        onClick={() => navigate("/pricing")}
        className="bg-blue-600 text-white hover:bg-blue-700"
      >
        View Plans
      </Button>
    </div>
  );
};
